import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { EnvelopeSimple, Lock, User } from '@phosphor-icons/react';
import BrandStripe, { CREAM_BG } from '@/components/auth/BrandStripe';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const user = await register(email, password, name);
      toast.success('Account created successfully!');
      if (user?.role === 'super_admin') {
        navigate('/admin');
      } else if (user?.role === 'sales_rep' || user?.role === 'dealer_user') {
        navigate('/my-home');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const formInner = (
    <div className="w-full max-w-md mx-auto space-y-8">
      <div className="lg:hidden flex items-center gap-3">
        <img src="/logo.svg" alt="LotMate" className="h-14 w-auto max-w-[12rem] object-contain object-left" />
        <span className="font-manrope font-bold text-xl tracking-tight">LotMate</span>
      </div>

      <div>
        <h2 className="font-manrope text-3xl font-extrabold tracking-tight">Create account</h2>
        <p className="text-muted-foreground mt-2">Get started with LotMate</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Min 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 font-semibold"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse-dot" />
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse-dot-delay-1" />
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse-dot-delay-2" />
                </div>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
          Sign in
        </Link>
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex flex-1 flex-col lg:flex-row min-h-0">
        <div className={`hidden lg:flex lg:w-1/2 flex-col min-h-0 ${CREAM_BG} items-center justify-center p-10`}>
          <div className="flex flex-col items-center gap-4 w-full max-w-lg">
            <img
              src="/logo.svg"
              alt="LotMate"
              className="w-full max-w-md h-auto max-h-[min(40vh,16rem)] object-contain"
            />
            <span className="font-manrope font-bold text-2xl tracking-tight text-foreground">LotMate</span>
          </div>
        </div>

        <div className={`w-full lg:w-1/2 flex flex-col flex-1 min-h-0 ${CREAM_BG}`}>
          <div className="flex-1 flex flex-col justify-start px-6 sm:px-10 py-8 lg:py-10 min-h-0">
            {formInner}
          </div>
        </div>
      </div>

      <BrandStripe variant="register" />
    </div>
  );
}
